import { useEffect, useState } from "react";
import {
  CiCloudOff,
  CiCloudOn,
  CiServer,
  CiWifiOff,
  CiWifiOn,
} from "react-icons/ci";
import { IoRefresh, IoWarning } from "react-icons/io5";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import formatDate from "../../utils/formatDate";

const StatusIcon = ({ status, type }) => {
  const baseClass = "w-10 h-10 flex justify-center items-center rounded-full";

  if (type === "network") {
    return (
      <div className={`${baseClass} ${status ? "bg-green-100" : "bg-red-100"}`}>
        {status ? (
          <CiWifiOn size={24} className="text-green-600" />
        ) : (
          <CiWifiOff size={24} className="text-red-600" />
        )}
      </div>
    );
  }

  if (type === "server") {
    if (status.maintenance) {
      return (
        <div className={`${baseClass} bg-amber-100`}>
          <IoWarning size={24} className="text-amber-600" />
        </div>
      );
    }
    return (
      <div
        className={`${baseClass} ${status.online ? "bg-green-100" : "bg-red-100"
          }`}
      >
        <CiServer
          size={24}
          className={status.online ? "text-green-600" : "text-red-600"}
        />
      </div>
    );
  }

  return null;
};

const PingIndicator = ({ ping }) => {
  let color = "text-green-600";
  if (ping > 300) color = "text-amber-600";
  if (ping > 600) color = "text-red-600";

  return (
    <div className="flex items-center space-x-1">
      <div className={`w-2 h-2 rounded-full ${color} animate-pulse`}></div>
      <span className="text-xs font-mono">{ping}ms</span>
    </div>
  );
};

export default function NetworkStatusBanner() {
  const { isOnline, wasOffline, serverStatus, refreshServerStatus } =
    useNetworkStatus();
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("network");

  const handleRefresh = async () => {
    await refreshServerStatus();
  };

  const showNetworkWarning = !isOnline;
  const showServerWarning =
    !serverStatus.loading && (!serverStatus.online || serverStatus.maintenance);

  useEffect(() => {
    // Automatically show the banner if the user was offline previously
    if (wasOffline || showNetworkWarning || showServerWarning) {
      setIsVisible(true);
    } else {
      if (isOnline) setIsVisible(false);
    }
  }, [wasOffline, showNetworkWarning, showServerWarning]);

  return (
    <div className="fixed bottom-14 left-4 z-30 font-sans max-w-md w-full">
      {/* Floating Status Button */}
      <button
        onClick={() => setIsVisible((prev) => !prev)}
        className={`absolute left-0 -bottom-12 bg-${serverStatus.online || serverStatus.maintenance || isOnline
            ? "green"
            : "red"
          }-100 hover:bg-gray-100 text-gray-700 p-2 rounded-full shadow-md transition-all border border-gray-200`}
      >
        <CiServer
          size={20}
          color={
            serverStatus.online || serverStatus.maintenance || isOnline
              ? "green"
              : "red"
          }
        />
      </button>

      {/* Main Status Panel */}
      {isVisible && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b border-gray-200">
            <div className="flex space-x-2">
              <button
                className={`px-3 py-1 rounded-lg text-sm font-medium ${activeTab === "network"
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}
                onClick={() => setActiveTab("network")}
              >
                Network
              </button>
              <button
                className={`px-3 py-1 rounded-lg text-sm font-medium ${activeTab === "server"
                    ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                    : "text-gray-600 hover:bg-gray-100"
                  }`}
                onClick={() => setActiveTab("server")}
              >
                Server
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {activeTab === "network" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <StatusIcon status={isOnline} type="network" />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {isOnline
                          ? "Network Connected"
                          : "No Internet Connection"}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {isOnline
                          ? "Your device is connected to the internet"
                          : "Check your network settings"}
                      </p>
                    </div>
                  </div>
                  {isOnline && (
                    <div className="flex items-center space-x-2">
                      <CiCloudOn size={20} className="text-green-600" />
                      <span className="text-xs text-gray-500">Online</span>
                    </div>
                  )}
                </div>

                {!isOnline && (
                  <button
                    onClick={handleRefresh}
                    className="w-full py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center space-x-2 transition-colors border border-red-100"
                  >
                    <IoRefresh size={16} />
                    <span>Retry Connection</span>
                  </button>
                )}
              </div>
            )}

            {activeTab === "server" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <StatusIcon status={serverStatus} type="server" />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {serverStatus.loading
                          ? "Checking Server Status..."
                          : serverStatus.maintenance
                            ? "Maintenance in Progress"
                            : serverStatus.online
                              ? "Server Online"
                              : "Server Offline"}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {serverStatus.loading
                          ? "Please wait..."
                          : serverStatus.maintenance
                            ? "We're performing scheduled maintenance"
                            : serverStatus.online
                              ? "All systems operational"
                              : "Unable to reach our servers"}
                      </p>
                    </div>
                  </div>
                  {!serverStatus.loading &&
                    serverStatus.online &&
                    !serverStatus.maintenance && (
                      <PingIndicator ping={serverStatus.ping} />
                    )}
                </div>

                <div className="space-y-2">
                  <button
                    onClick={handleRefresh}
                    className="w-full py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center space-x-2 transition-colors border border-blue-100"
                  >
                    <IoRefresh size={16} />
                    <span>Refresh Status</span>
                  </button>

                  {serverStatus.maintenance && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <div className="flex items-start space-x-2">
                        <IoWarning
                          size={18}
                          className="text-amber-600 mt-0.5"
                        />
                        <div>
                          <h4 className="font-medium text-amber-700">
                            Scheduled Maintenance
                          </h4>
                          <p className="text-xs text-amber-600">
                            Expected completion:{" "}
                            {formatDate(serverStatus?.maintenanceEnd, true)} UTC
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 flex justify-between border-t border-gray-200">
            <span>
              Last checked:{" "}
              {serverStatus.lastChecked
                ? new Date(serverStatus.lastChecked).toLocaleTimeString()
                : "Never"}
            </span>
            <span>v{process.env.REACT_APP_VERSION}</span>
          </div>
        </div>
      )}

      {/* Critical Warning Banners */}
      {isVisible && showNetworkWarning && (
        <div className="mt-4 animate-fade-in">
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CiWifiOff size={24} className="text-red-600" />
                <span>No internet connection detected</span>
              </div>
              <button
                onClick={handleRefresh}
                className="p-1.5 bg-red-100 hover:bg-red-200 rounded-full text-red-600"
              >
                <IoRefresh size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {isVisible && showServerWarning && (
        <div className="mt-4 animate-fade-in">
          <div
            className={`${serverStatus.maintenance
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-red-50 border-red-200 text-red-800"
              } border p-4 rounded-xl shadow-sm`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {serverStatus.maintenance ? (
                  <IoWarning size={24} className="text-amber-600" />
                ) : (
                  <CiCloudOff size={24} className="text-red-600" />
                )}
                <span>
                  {serverStatus.maintenance
                    ? "Server maintenance in progress"
                    : "Server connection failed"}
                </span>
              </div>
              <button
                onClick={handleRefresh}
                className={`p-1.5 rounded-full ${serverStatus.maintenance
                    ? "bg-amber-100 hover:bg-amber-200 text-amber-600"
                    : "bg-red-100 hover:bg-red-200 text-red-600"
                  }`}
              >
                <IoRefresh size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
